import { memo, type CSSProperties, type ReactNode } from 'react';
import { getBaseStatus } from '@/utils/projectStatuses';
import { getStatusBadgeColorByBase } from '@/styles/projectColorTokens';
import {
  getDeadlineDaysLeft,
  getDayWordRu,
  getRemainingHeadlineRu,
  getDeadlineRemainingBadgeStyle,
} from '@/utils/projectDeadlineRemaining';
import './DeadlineRemainingBadge.css';

const ADDED_BADGE_PALETTE = getStatusBadgeColorByBase('Добавлен');

export interface DeadlineRemainingBadgeProps {
  deadline: string | null | undefined;
  projectState: string | null | undefined;
  todayLabel?: string;
  className?: string;
}

/**
 * Бейдж срока в комбо/меню: без даты не рендерится; иначе «Осталось n дней», просрочка, сегодня.
 * Спокойные варианты — те же фон/цвет текста, что у статусного бейджа «Добавлен».
 */
export const DeadlineRemainingBadge = memo(function DeadlineRemainingBadge({
  deadline,
  projectState,
  todayLabel = 'Дедлайн сегодня',
  className,
}: DeadlineRemainingBadgeProps) {
  const isCompleted = getBaseStatus(projectState) === 'Готов';
  if (isCompleted) return null;

  const daysLeft = deadline?.trim() ? getDeadlineDaysLeft(deadline) : null;
  const style = getDeadlineRemainingBadgeStyle(deadline, daysLeft);

  const likeAddedStyle: CSSProperties = {
    backgroundColor: ADDED_BADGE_PALETTE.bg,
    color: ADDED_BADGE_PALETTE.color,
  };

  const extra = className ? ` ${className}` : '';

  if (style === 'no_deadline' || daysLeft === null || Number.isNaN(daysLeft)) return null;

  const isOverdue = daysLeft < 0;
  const isToday = daysLeft === 0;

  let inner: ReactNode;
  if (isOverdue) {
    inner = (
      <>
        Просрочен на{' '}
        <strong>{Math.abs(daysLeft)}</strong>
        {' '}
        {getDayWordRu(daysLeft)}
      </>
    );
  } else if (isToday) {
    inner = <span className="deadline-remaining-badge__today">{todayLabel}</span>;
  } else {
    inner = (
      <>
        {getRemainingHeadlineRu(daysLeft)}
        {' '}
        <strong>{daysLeft}</strong>
        {' '}
        {getDayWordRu(daysLeft)}
      </>
    );
  }

  const calmAdded = style === 'white';
  return (
    <span
      className={`deadline-remaining-badge deadline-remaining-badge_${style}${calmAdded ? ' deadline-remaining-badge_like-added' : ''}${extra}`}
      style={calmAdded ? likeAddedStyle : undefined}
    >
      {inner}
    </span>
  );
});
